import { ref } from 'vue';
// The database-menu contract replaces the former synthetic tooling menu.
export const MENU_CACHE_VERSION = 2;
const META_KEY = 'routerst.contract';
const TOOLING_ROUTE = '/tooling_product_list';
const revision = ref(0);

function scope(storage) {
  return JSON.stringify(['userid', 'oid', 'moid'].map(key => storage.getItem(key) || ''));
}
function parse(raw, fallback) {
  try { return JSON.parse(raw) ?? fallback; } catch { return fallback; }
}
function current(storage, raw) {
  const meta = parse(storage.getItem(META_KEY), {});
  return meta.version === MENU_CACHE_VERSION && meta.scope === scope(storage) && meta.raw === raw;
}
function filterTree(rows, allowTooling) {
  return rows.filter(row => row && typeof row === 'object')
    .filter(row => Number(row.id) !== -90001 && (row.links !== TOOLING_ROUTE || (allowTooling && Number(row.id) === 191)))
    .map(row => Array.isArray(row.child) ? { ...row, child: filterTree(row.child, allowTooling) } : row)
    .filter(row => !Array.isArray(row.child) || row.child.length > 0);
}
function hasLegacy(rows) {
  return rows.some(row => Number(row?.id) === -90001
    || (row?.links === TOOLING_ROUTE && Number(row.id) !== 191)
    || (Array.isArray(row?.child) && hasLegacy(row.child)));
}
export function isMenuCacheCurrent(storage = window.localStorage) {
  try {
    const raw = storage.getItem('routerst');
    return raw != null && Array.isArray(parse(raw, null)) && current(storage, raw);
  } catch { return false; }
}
export function readMenuTree(storage = window.localStorage) {
  // Track permission changes in existing computed properties such as canWrite.
  void revision.value;
  try {
    const raw = storage.getItem('routerst');
    const rows = parse(raw, []);
    return Array.isArray(rows) ? filterTree(rows, current(storage, raw)) : [];
  } catch { return []; }
}
export function saveMenuTree(rows, storage = window.localStorage, notify = () => window.dispatchEvent(new Event('routerstUpdated'))) {
  if (!Array.isArray(rows)) throw new TypeError('Invalid menu response');
  const legacy = hasLegacy(rows);
  const raw = JSON.stringify(filterTree(rows, !legacy));
  // Invalidate first: an interrupted write can never authorize an old menu.
  try {
    storage.removeItem(META_KEY);
    storage.setItem('routerst', raw);
    if (!legacy) storage.setItem(META_KEY, JSON.stringify({ version: MENU_CACHE_VERSION, scope: scope(storage), raw }));
  } finally {
    revision.value++;
    notify();
  }
  return !legacy;
}
export function invalidateMenuCache(storage = window.localStorage,
  notify = () => window.dispatchEvent(new Event('routerstUpdated'))) {
  try { storage.removeItem(META_KEY); } catch { /* Reads fail closed. */ }
  revision.value++;
  notify();
}

const pending = new Map();
export function refreshMenuTree(fetchMenus, identity, storage = window.localStorage,
  notify = () => window.dispatchEvent(new Event('routerstUpdated'))) {
  const initialIdentity = identity();
  const initialScope = scope(storage);
  const key = initialIdentity + ':' + initialScope;
  if (pending.has(key)) return pending.get(key);
  const task = Promise.resolve().then(fetchMenus).then(rows => {
    if (identity() !== initialIdentity || scope(storage) !== initialScope) return false;
    return saveMenuTree(rows, storage, notify);
  }).finally(() => pending.delete(key));
  pending.set(key, task);
  return task;
}
